import { useState, useRef } from 'react';
import axios from 'axios';

const Backups = () => {
	const [isExporting, setIsExporting] = useState(false)
	const [exportResult, setExportResult] = useState(null)

	// Import state
	const [selectedFile, setSelectedFile] = useState(null)
	const [importStep, setImportStep] = useState('idle') // idle | selected | confirming | importing
	const [importResult, setImportResult] = useState(null)
	const fileInputRef = useRef(null)

	const handleExport = async () => {
		setIsExporting(true)
		setExportResult(null)
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_WEB_URL}api/backup/export`,
				{
					withCredentials: true,
					responseType: 'blob',
				}
			)

			// Create a download link from the blob
			const blob = new Blob([response.data], { type: 'application/xml' })
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `hammer-the-backlog-backup-${new Date().toISOString().slice(0, 10)}.xml`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)

			setExportResult({ success: true, message: 'Backup downloaded successfully!' })
		} catch (error) {
			setExportResult({ success: false, message: 'Failed to export backup. Please try again.' })
		} finally {
			setIsExporting(false)
		}
	}

	const handleFileSelect = (e) => {
		const file = e.target.files[0]
		if (!file) return
		if (!file.name.endsWith('.xml')) {
			setImportResult({ success: false, message: 'Please select an XML file.' })
			return
		}
		setSelectedFile(file)
		setImportStep('selected')
		setImportResult(null)
	}

	const handleImportClick = () => {
		setImportResult(null)
		fileInputRef.current.click()
	}

	const handleConfirm = async () => {
		setImportStep('importing')
		setImportResult(null)
		try {
			const xmlData = await selectedFile.text()
			const response = await axios.post(
				`${process.env.REACT_APP_WEB_URL}api/backup/import`,
				{ xmlData },
				{ withCredentials: true }
			)

			const { imported } = response.data
			const summary = Object.entries(imported)
				.map(([key, count]) => `${count} ${key}`)
				.join(', ')

			setImportResult({
				success: true,
				message: `Backup imported successfully! Restored: ${summary}.`,
			})
		} catch (error) {
			const msg = error.response?.data?.message || 'Failed to import backup. Please try again.'
			setImportResult({ success: false, message: msg })
		} finally {
			setSelectedFile(null)
			setImportStep('idle')
			if (fileInputRef.current) fileInputRef.current.value = ''
		}
	}

	const handleCancel = () => {
		setSelectedFile(null)
		setImportStep('idle')
		setImportResult(null)
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	return (
		<div id="backups" className="backups">
			<h1>Backups</h1>
			<p className="backups__description">
				Download a full XML backup of all your data — models, goals, years, quarters, roundups, and more.
				You can also restore from a previously exported backup.
			</p>

			{/* ---- EXPORT ---- */}
			<div className="backups__section">
				<div className="backups__card">
					<div className="backups__card-icon">
						<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
							<polyline points="7 10 12 15 17 10" />
							<line x1="12" y1="15" x2="12" y2="3" />
						</svg>
					</div>
					<div className="backups__card-content">
						<h2>Export Backup</h2>
						<p>Downloads an XML file containing all of your data across every collection.</p>
					</div>
					<button
						id="export-backup-btn"
						className={`backups__export-btn ${isExporting ? 'backups__export-btn--loading' : ''}`}
						onClick={handleExport}
						disabled={isExporting}
					>
						{isExporting ? (
							<>
								<span className="backups__spinner"></span>
								Exporting…
							</>
						) : (
							'Download XML Backup'
						)}
					</button>
				</div>

				{exportResult && (
					<div className={`backups__result ${exportResult.success ? 'backups__result--success' : 'backups__result--error'}`}>
						{exportResult.message}
					</div>
				)}
			</div>

			{/* ---- IMPORT ---- */}
			<div className="backups__section">
				<div className="backups__card">
					<div className="backups__card-icon backups__card-icon--import">
						<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
							<polyline points="17 8 12 3 7 8" />
							<line x1="12" y1="3" x2="12" y2="15" />
						</svg>
					</div>
					<div className="backups__card-content">
						<h2>Import Backup</h2>
						<p>Restore your data from a previously exported XML backup file.</p>
					</div>

					{/* Hidden file input */}
					<input
						type="file"
						accept=".xml"
						ref={fileInputRef}
						onChange={handleFileSelect}
						style={{ display: 'none' }}
						id="import-file-input"
					/>

					{/* Step: idle — show pick file button */}
					{importStep === 'idle' && (
						<button
							id="import-backup-btn"
							className="backups__import-btn"
							onClick={handleImportClick}
						>
							Import Backup
						</button>
					)}

					{/* Step: selected — show file name + confirm warning */}
					{importStep === 'selected' && (
						<div className="backups__confirm-zone">
							<p className="backups__file-name">
								Selected: <strong>{selectedFile?.name}</strong>
							</p>
							<div className="backups__warning">
								<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
									<line x1="12" y1="9" x2="12" y2="13" />
									<line x1="12" y1="17" x2="12.01" y2="17" />
								</svg>
								<span>ALL of your existing data will be permanently deleted and replaced by this backup.</span>
							</div>
							<div className="backups__confirm-actions">
								<button
									id="confirm-import-btn"
									className="backups__confirm-btn"
									onClick={handleConfirm}
								>
									Confirm Import
								</button>
								<button
									id="cancel-import-btn"
									className="backups__cancel-btn"
									onClick={handleCancel}
								>
									Cancel
								</button>
							</div>
						</div>
					)}

					{/* Step: importing — show spinner */}
					{importStep === 'importing' && (
						<div className="backups__importing">
							<span className="backups__spinner"></span>
							<span>Importing backup…</span>
						</div>
					)}
				</div>

				{importResult && (
					<div className={`backups__result ${importResult.success ? 'backups__result--success' : 'backups__result--error'}`}>
						{importResult.message}
					</div>
				)}
			</div>
		</div>
	);
}

export default Backups
